/**
 * Master Bot - My Bots Page
 * List and manage all created bots
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import { useTelegram } from '../../hooks/useTelegram';

const BotList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const BotCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
`;

const BotHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const BotInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const BotName = styled.div`
  font-size: var(--font-lg);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
`;

const BotUsername = styled.div`
  font-size: var(--font-md);
  color: var(--text-secondary);
`;

const BotStatus = styled.span<{ active: boolean }>`
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: var(--radius-md);
  font-size: var(--font-sm);
  font-weight: var(--font-medium);
  background: ${({ active }) => (active ? 'rgba(76, 175, 80, 0.2)' : 'rgba(142, 142, 147, 0.2)')};
  color: ${({ active }) => (active ? 'var(--accent-green)' : 'var(--text-secondary)')};
`;

const BotStats = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-md);
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--radius-md);
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-xs);
`;

const StatValue = styled.div`
  font-size: var(--font-lg);
  font-weight: var(--font-bold);
  color: var(--text-primary);
`;

const StatLabel = styled.div`
  font-size: var(--font-xs);
  color: var(--text-secondary);
  text-align: center;
`;

const BotActions = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--spacing-sm);
`;

const EmptyState = styled.div`
  text-align: center;
  padding: var(--spacing-xl);
  color: var(--text-secondary);
`;

const MyBots: React.FC = () => {
  const navigate = useNavigate();
  const { showMainButton, showBackButton } = useTelegram();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedBot, setSelectedBot] = useState<any>(null);
  const [bots, setBots] = useState([
    {
      id: 1,
      name: 'My Store Bot',
      username: '@mystorebot',
      active: true,
      users: 150,
      orders: 45,
      revenue: '$1,234',
    },
    {
      id: 2,
      name: 'Shop Assistant',
      username: '@shopassistant',
      active: false,
      users: 23,
      orders: 5,
      revenue: '$156',
    },
  ]);

  useEffect(() => {
    showMainButton('Create New Bot', () => navigate('/master/create-bot'));
    showBackButton(() => navigate('/master/dashboard'));
  }, [showMainButton, showBackButton, navigate]);

  const handleDeleteBot = (bot: any) => {
    setSelectedBot(bot);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (selectedBot) {
      setBots(bots.filter((b) => b.id !== selectedBot.id));
      setShowDeleteModal(false);
      setSelectedBot(null);
    }
  };

  return (
    <Layout header={<Navbar title="My Bots" showBack />}>
      {bots.length === 0 ? (
        <EmptyState>
          <p>No bots created yet.</p>
          <p style={{ marginTop: 'var(--spacing-md)' }}>Tap the button below to create your first bot!</p>
        </EmptyState>
      ) : (
        <BotList>
          {bots.map((bot) => (
            <BotCard key={bot.id}>
              <BotHeader>
                <BotInfo>
                  <BotName>{bot.name}</BotName>
                  <BotUsername>{bot.username}</BotUsername>
                </BotInfo>
                <BotStatus active={bot.active}>{bot.active ? 'Active' : 'Inactive'}</BotStatus>
              </BotHeader>

              <BotStats>
                <StatItem>
                  <StatValue>{bot.users}</StatValue>
                  <StatLabel>Users</StatLabel>
                </StatItem>
                <StatItem>
                  <StatValue>{bot.orders}</StatValue>
                  <StatLabel>Orders</StatLabel>
                </StatItem>
                <StatItem>
                  <StatValue>{bot.revenue}</StatValue>
                  <StatLabel>Revenue</StatLabel>
                </StatItem>
              </BotStats>

              <BotActions>
                <Button size="small" onClick={() => navigate(`/vendor/${bot.username}/admin/dashboard`)}>
                  ⚙️ Manage
                </Button>
                <Button size="small" variant="secondary" onClick={() => navigate('/master/subscription')}>
                  📈 Plan
                </Button>
                <Button size="small" variant="danger" onClick={() => handleDeleteBot(bot)}>
                  🗑️ Delete
                </Button>
              </BotActions>
            </BotCard>
          ))}
        </BotList>
      )}

      <Modal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Bot"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmDelete}>
              Delete
            </Button>
          </>
        }
      >
        <p>Are you sure you want to delete {selectedBot?.name}?</p>
        <p style={{ marginTop: 'var(--spacing-md)', color: 'var(--text-secondary)', fontSize: 'var(--font-sm)' }}>
          This action cannot be undone. All bot data will be permanently deleted.
        </p>
      </Modal>
    </Layout>
  );
};

export default MyBots;
